import { AnyVariables, useQuery, UseQueryArgs } from "urql";

import {
  useHandleUrqlResponse,
  UseHandleUrqlResponseArgs,
} from "./useHandleUrqlResponse";

/** Recursively allows optional keys and undefined in values (for use when pause is true). */
type VariablesWithOptionalValues<V> = {
  [K in keyof V]?: V[K] extends object
    ? VariablesWithOptionalValues<V[K]>
    : V[K] | undefined;
};

/** When pause is true, variables may be omitted or partial (including undefined values) since the query does not run. */
export type UseQueryWithResponseHandlerQueryArgs<
  Variables extends AnyVariables,
  Data,
> = Omit<UseQueryArgs<Variables, Data>, "variables" | "pause"> & {
  pause?: boolean;
  /** Required when pause is false; optional when pause is true. Values may be undefined when paused. */
  variables?: VariablesWithOptionalValues<Variables> | Variables;
};

const useQueryWithResponseHandlerImpl = <
  Data extends { [key in string]?: any },
  Variables extends AnyVariables = AnyVariables,
>(
  queryArgs: UseQueryWithResponseHandlerQueryArgs<Variables, Data>,
  handlerArgs: Omit<UseHandleUrqlResponseArgs<Data, Variables>, "state">,
) => {
  const normalizedQueryArgs: UseQueryArgs<Variables, Data> = {
    ...queryArgs,
    variables: (queryArgs.variables ?? {}) as Variables,
  };
  const [state, execute] = useQuery(normalizedQueryArgs);
  return {
    state,
    execute,
    ...useHandleUrqlResponse({
      state,
      ...handlerArgs,
    }),
  };
};

type UseQueryWithResponseHandlerReturn<
  Data extends { [key in string]?: any },
  Variables extends AnyVariables,
> = ReturnType<typeof useQueryWithResponseHandlerImpl<Data, Variables>>;

type UseQueryWithResponseHandler = {
  <
    Data extends { [key in string]?: any },
    Variables extends AnyVariables = AnyVariables,
  >(
    queryArgs: UseQueryWithResponseHandlerQueryArgs<Variables, Data>,
    handlerArgs?: Omit<UseHandleUrqlResponseArgs<Data, Variables>, "state">,
  ): UseQueryWithResponseHandlerReturn<Data, Variables>;
};

export const useQueryWithResponseHandler: UseQueryWithResponseHandler = (
  queryArgs,
  handlerArgs = {},
) => useQueryWithResponseHandlerImpl(queryArgs, handlerArgs);
